import { map, switchMap } from 'rxjs/operators';
import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, Observable, Subject } from 'rxjs';
import { UploadFileType } from 'src/assets/data/enum/uploadFileType';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from './authentication.service';

@Injectable()
export class UploadService {
  private apiBaseUrl = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private zone: NgZone
  ) {}

  // TODO Per Multi farm???
  saveFile(file): Observable<any> {
    return this.http
      .post(this.apiBaseUrl + 'api/private/v1/upload/files', file)
      .pipe(map((response) => response));
  }

  // Public API: keep the same name/signature.
  // It prepares the file (fix KML LineString → Polygon if needed) and then streams it.
  importFieldBatchToBackend(file: File): Observable<any> {
    return from(this.convertLineStringToPolygon(file)).pipe(
      switchMap((preparedFile) => this.uploadFieldsStream(preparedFile))
    );
  }

  // Private: original streaming upload implementation (moved here unchanged).
  private uploadFieldsStream(file: File): Observable<any> {
    const subject = new Subject<any>();
    const tokenData = this.authenticationService.getAuthToken();

    // Basic auth check
    if (!tokenData?.access_token) {
      subject.error('Access token missed.');
      return subject.asObservable();
    }

    // Build form payload
    const formData = new FormData();
    formData.append('file', file);

    // Infer type from file extension (fallback to 'zip' if not allowed)
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const validTypes = new Set<string>(Object.values(UploadFileType));
    const type = validTypes.has(extension) ? extension : 'zip';

    // Company ID from localStorage (can be null)
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;

    // Build URL
    const url = `${this.apiBaseUrl}api/private/v1/utils/satelliteShapeImporter/importFields?type=${type}&companyIds=${companyId}&multiFarm=true`;

    // Start fetch streaming request
    fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokenData.access_token}`,
      },
      body: formData,
    })
      .then(async (response) => {
        // If no body (no stream), try to parse a JSON error, otherwise emit generic error
        if (!response.body) {
          const text = await response.text();
          try {
            const parsed = JSON.parse(text);
            this.zone.run(() => subject.error(parsed.error || 'Error'));
          } catch (e) {
            console.error('Error parsing JSON fallback:', e);
            console.warn('Non-JSON content:', text);
            this.zone.run(() => subject.error('Generic error'));
          }
          return;
        }

        // Stream reader
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // Recursive stream reading loop
        const readChunk = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              // Flush the last buffered JSON line if present
              this.processBuffer(buffer, subject);
              subject.complete();
              return;
            }

            // Decode current chunk and split by newline
            const chunkText = decoder.decode(value, { stream: true });
            buffer += chunkText;
            const parts = buffer.split('\n');
            buffer = parts.pop() || '';

            // Process each complete line
            parts.forEach((line) => {
              if (!line.trim()) return;

              // Remove SSE 'data:' prefix if present
              const cleaned = line.startsWith('data:')
                ? line.replace(/^data:\s*/, '')
                : line.trim();

              try {
                // Accept JSON object/array lines
                if (cleaned.startsWith('{') || cleaned.startsWith('[')) {
                  const parsed = JSON.parse(cleaned);
                  this.zone.run(() => subject.next(parsed));
                } else {
                  // Non-JSON line: emit as error to keep current behavior
                  console.warn('Non-JSON stream chunk received:', cleaned);
                  this.zone.run(() => subject.error(cleaned));
                }
              } catch (e) {
                console.error('Error parsing stream chunk:', e);
                console.warn('Failed content:', cleaned);
                this.zone.run(() => subject.error('Error in parsing server response.'));
              }
            });

            // Continue reading
            readChunk();
          });
        };

        readChunk();
      })
      .catch((error) => {
        // Network or unexpected error
        console.error('Fetch error:', error);
        this.zone.run(() => subject.error(error));
      });

    return subject.asObservable();
  }

  importSubunitBatchToBackend(file: File, cropId: number): Observable<any> {
    const subject = new Subject<any>();
    const tokenData = this.authenticationService.getAuthToken();

    if (!tokenData?.access_token) {
      subject.error('Access token missed.');
      return subject.asObservable();
    }

    const formData = new FormData();
    formData.append('file', file);

    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const validTypes = new Set<string>(Object.values(UploadFileType));
    const type = validTypes.has(extension) ? extension : 'zip';
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;

    const url = `${this.apiBaseUrl}api/private/v1/utils/satelliteShapeImporter/importSubUnit?type=${type}&cropId=${cropId}&companyIds=${companyId}&multiFarm=true`;

    fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokenData.access_token}`,
      },
      body: formData,
    })
      .then(async (response) => {
        if (!response.body) {
          const text = await response.text();
          try {
            const parsed = JSON.parse(text);
            this.zone.run(() => subject.error(parsed.error || 'Error'));
          } catch (e) {
            console.error('Error parsing JSON fallback:', e);
            console.warn('Content non JSON:', text);
            this.zone.run(() => subject.error('Generic error'));
          }
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const readChunk = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              this.processBuffer(buffer, subject);
              subject.complete();
              return;
            }

            const chunkText = decoder.decode(value, { stream: true });
            buffer += chunkText;
            const parts = buffer.split('\n');
            buffer = parts.pop() || '';

            parts.forEach((line) => {
              if (!line.trim()) return;

              const cleaned = line.startsWith('data:')
                ? line.replace(/^data:\s*/, '')
                : line.trim();

              try {
                if (cleaned.startsWith('{') || cleaned.startsWith('[')) {
                  const parsed = JSON.parse(cleaned);
                  this.zone.run(() => subject.next(parsed));
                } else {
                  console.warn('Chunk non JSON riceived:', cleaned);
                  this.zone.run(() => subject.error(cleaned));
                }
              } catch (e) {
                console.error('Error parsing chunk:', e);
                console.warn('Content failed:', cleaned);
                this.zone.run(() => subject.error('Error in parsing server response.'));
              }
            });

            readChunk();
          });
        };

        readChunk();
      })
      .catch((error) => {
        console.error('Errore fetch:', error);
        this.zone.run(() => subject.error(error));
      });

    return subject.asObservable();
  }

  importSubUnitPreview(file: File): Observable<any> {
    const subject = new Subject<any>();
    const tokenData = this.authenticationService.getAuthToken();

    if (!tokenData?.access_token) {
      subject.error('Access token missed.');
      return subject.asObservable();
    }

    const formData = new FormData();
    formData.append('file', file);

    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const validTypes = new Set<string>(Object.values(UploadFileType));
    const type = validTypes.has(extension) ? extension : 'zip';

    let companySelected: any = null;
    try {
      companySelected = JSON.parse(localStorage.getItem('companySelected') || 'null');
    } catch (e) {
      console.warn('Errore parsing companySelected da localStorage:', e);
      companySelected = null;
    }
    const companyId = companySelected?.id ?? null;

    const url = `${this.apiBaseUrl}api/private/v1/utils/satelliteShapeImporter/importSubUnitPreview?type=${type}&companyIds=${companyId}&multiFarm=true`;

    fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokenData.access_token}`,
      },
      body: formData,
    })
      .then(async (response) => {
        if (!response.body) {
          const text = await response.text();
          try {
            const parsed = JSON.parse(text);
            this.zone.run(() => subject.error(parsed.error || 'Error'));
          } catch (e) {
            console.error('Error parsing JSON fallback:', e);
            console.warn('Content non JSON:', text);
            this.zone.run(() => subject.error('Generic error'));
          }
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const readChunk = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              this.processBuffer(buffer, subject);
              subject.complete();
              return;
            }

            const chunkText = decoder.decode(value, { stream: true });
            buffer += chunkText;
            const parts = buffer.split('\n');
            buffer = parts.pop() || '';

            parts.forEach((line) => {
              if (!line.trim()) return;

              const cleaned = line.startsWith('data:')
                ? line.replace(/^data:\s*/, '')
                : line.trim();

              try {
                if (cleaned.startsWith('{') || cleaned.startsWith('[')) {
                  const parsed = JSON.parse(cleaned);
                  this.zone.run(() => subject.next(parsed));
                } else {
                  console.warn('Chunk non JSON ricevuto:', cleaned);
                  this.zone.run(() => subject.error(cleaned));
                }
              } catch (e) {
                console.error('Errore parsing chunk:', e);
                console.warn('Contenuto fallito:', cleaned);
                this.zone.run(() => subject.error('Errore nel parsing della risposta server.'));
              }
            });

            readChunk();
          });
        };

        readChunk();
      })
      .catch((error) => {
        console.error('Errore fetch:', error);
        this.zone.run(() => subject.error(error));
      });

    return subject.asObservable();
  }

  private processBuffer(buffer: string, subject: Subject<any>) {
    if (buffer.trim()) {
      try {
        const cleaned = buffer.replace(/^data:\s*/, '');
        const chunk = JSON.parse(cleaned);
        subject.next(chunk);
      } catch (e) {
        console.error('Errore parsing buffer final', e);
      }
    }
  }

  // Converts a KML that contains a single closed LineString into a minimal Polygon KML.
  // If the file is already a Polygon or not a KML, returns the original file.
  // Lightweight string-based parsing; no DOM APIs used.
  private async convertLineStringToPolygon(file: File): Promise<File> {
    // Only act on .kml files
    if (!/\.kml$/i.test(file.name)) return file;

    const text = await file.text();
    const lower = text.toLowerCase();

    // Already contains a Polygon → do nothing
    if (lower.includes('<polygon')) return file;

    // No LineString found → do nothing
    const lsStart = lower.indexOf('<linestring');
    const lsEnd = lower.indexOf('</linestring>');
    if (lsStart === -1 || lsEnd === -1) return file;

    // Extract LineString block and coordinates content
    const lsBlock = text.slice(lsStart, lsEnd + '</LineString>'.length);
    const coordMatch = lsBlock.match(/<coordinates[^>]*>([\s\S]*?)<\/coordinates>/i);
    if (!coordMatch) return file;

    // Parse lon,lat[,alt] tokens
    const tokens = (coordMatch[1] || '').trim().split(/\s+/).filter(Boolean);
    if (!tokens.length) return file;

    const ring: Array<[number, number]> = [];
    for (const t of tokens) {
      const [lonStr, latStr] = t.split(',');
      const lon = Number(lonStr),
        lat = Number(latStr);
      if (Number.isFinite(lon) && Number.isFinite(lat)) ring.push([lon, lat]);
    }
    // Not enough points to form a polygon
    if (ring.length < 3) return file;

    // Ensure the ring is closed (first point equals the last)
    const [lon0, lat0] = ring[0];
    const [lone, late] = ring[ring.length - 1];
    if (lon0 !== lone || lat0 !== late) ring.push([lon0, lat0]);

    // Try to reuse a <name>; fallback to filename base
    const nameMatch = text.match(/<name[^>]*>([\s\S]*?)<\/name>/i);
    const name = nameMatch ? nameMatch[1].trim() : file.name.replace(/\.kml$/i, '');

    // Build a minimal KML with Polygon (altitude set to 0)
    const polygonCoords = ring.map(([x, y]) => `${x},${y},0`).join(' ');
    const fixedKml =
      '<?xml version="1.0" encoding="UTF-8"?>' +
      '<kml xmlns="http://www.opengis.net/kml/2.2">' +
      `<Document><name>${this.escapeXml(name)}</name>` +
      `<Placemark><name>${this.escapeXml(name)} Polygon</name>` +
      '<Polygon><outerBoundaryIs><LinearRing>' +
      `<coordinates>${polygonCoords}</coordinates>` +
      '</LinearRing></outerBoundaryIs></Polygon>' +
      '</Placemark></Document></kml>';

    return new File([fixedKml], file.name.replace(/\.kml$/i, '_poly.kml'), {
      type: 'application/vnd.google-earth.kml+xml',
    });
  }

  // Simple XML escaping helper
  private escapeXml(s: string) {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
